import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import React from "react";
import { useParams } from "react-router-dom";

import { getReceivingOrder } from "@/api/receiving";
import { fmtDate } from "@/lib/format";
import { LoadState, useGoBack } from "@/lib/scan-ui/screen";
import { DeliveriesBlock } from "@/pages/receiving/DeliveriesBlock";
import { GoodsBadge } from "@/pages/receiving/goods";

/**
 * Заказ поставщику в НОВОЙ приёмке: список приходов (УПД) + что должно приехать.
 *
 * Отдельный экран, а не правка старого, специально: старая приёмка должна
 * продолжать работать нетронутой, пока новую обкатывают. Вся работа идёт
 * дальше, на экране прихода (`DeliveryPage`) — здесь только вход в него.
 */
export function ReceivingNewOrderPage(): React.ReactElement {
  const { ref = "" } = useParams<{ ref: string }>();
  const goBack = useGoBack("/receiving");

  const q = useQuery({
    queryKey: ["receiving-order", ref],
    queryFn: () => getReceivingOrder(ref),
    enabled: Boolean(ref),
  });
  const header = q.data?.header;

  return (
    <div className="flex h-app flex-col bg-slate-100">
      <div className="flex h-[46px] items-center gap-1 bg-slate-900 px-1 text-white">
        <button
          type="button"
          onClick={goBack}
          className="flex size-9 items-center justify-center"
          aria-label="Назад"
        >
          <ArrowLeft className="size-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-bold">
            ПРИЁМКА {header ? `· ${header.number}` : ""}
          </div>
          <div className="truncate text-[10px] text-slate-400">
            {header
              ? `${header.kontragent_name || "—"}${
                  header.receiving_date ? ` · приход ${fmtDate(header.receiving_date)}` : ""
                }`
              : "загрузка…"}
          </div>
        </div>
      </div>

      {!q.data ? (
        <LoadState
          isLoading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="space-y-2 p-2 pb-6">
            <div className="rounded-lg border bg-white p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[15px] font-bold">{header!.number}</span>
                {header!.state_name && (
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                    {header!.state_name}
                  </span>
                )}
                <GoodsBadge goods={header!.goods} />
              </div>
              <div className="mt-1 text-[12px] text-slate-600">
                {header!.organization_name}
              </div>
            </div>

            <DeliveriesBlock orderRef={ref} />

            <div className="rounded-lg border bg-white p-3">
              <div className="mb-2 text-[13px] font-semibold">Должно приехать</div>
              <table className="w-full text-[12px]">
                <tbody>
                  {q.data.lines.map((l) => (
                    <tr
                      key={`${l.nomenclature_ref}-${l.characteristic_ref}`}
                      className="border-t first:border-t-0"
                    >
                      <td className="py-1 pr-2">
                        <div className="font-medium">{l.nomenclature_name}</div>
                        <div className="text-slate-500">
                          {l.nomenclature_article}
                          {l.characteristic_name ? ` · ${l.characteristic_name}` : ""}
                        </div>
                      </td>
                      <td className="whitespace-nowrap py-1 text-right">
                        {l.qty} {l.unit_name}
                        {l.qty_remaining != null && l.qty_remaining !== l.qty && (
                          <div className="text-amber-700">осталось {l.qty_remaining}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReceivingNewOrderPage;
